/* AG Library, staff admin tool.
   Add a book (PDF -> WebP in-browser -> R2 -> create product) and manage/edit
   existing books (title, price, compare-at, preview, category, replace PDF). */
(function () {
  "use strict";
  var CFG = window.AG_LIB_ADMIN || {};
  var WORKER = (CFG.worker || "").replace(/\/+$/, "");
  var PW = "";
  var CATS = [];
  var root;

  function ready(fn) { document.readyState !== "loading" ? fn() : document.addEventListener("DOMContentLoaded", fn); }
  function el(h) { var d = document.createElement("div"); d.innerHTML = h.trim(); return d.firstChild; }
  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) { return ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]; }); }
  function pad4(n) { return ("0000" + n).slice(-4); }
  function slug(s) { return String(s).toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, ""); }
  function toWebp(c, q) { return new Promise(function (r) { c.toBlob(r, "image/webp", q); }); }

  /* ── API ──────────────────────────────────────────────────────────────── */
  // With the OTP shell, every ag-library call routes through the auth Worker's
  // /api/lib proxy (server-side password injection). Without it (legacy
  // lock-screen), hit ag-library directly with the typed password.
  function proxyOn() { return !!(window.AG_ADMIN && typeof window.AG_ADMIN.lib === "function"); }

  async function ping(pw) {
    if (proxyOn()) { var pr = await window.AG_ADMIN.lib("/admin/ping", { method: "POST", headers: { "content-type": "application/json" }, body: "{}" }); return pr.ok; }
    var r = await fetch(WORKER + "/admin/ping", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ password: pw }) }); return r.ok;
  }
  async function uploadBlob(book, path, blob, type) {
    var qs = "book=" + encodeURIComponent(book) + "&path=" + encodeURIComponent(path);
    var r;
    if (proxyOn()) {
      r = await window.AG_ADMIN.lib("/admin/upload?" + qs, { method: "POST", headers: { "content-type": type }, body: blob });
    } else {
      r = await fetch(WORKER + "/admin/upload?password=" + encodeURIComponent(PW) + "&" + qs, { method: "POST", headers: { "content-type": type }, body: blob });
    }
    if (!r.ok) throw new Error("upload failed: " + path + " (" + r.status + ")");
  }
  async function listBooks() {
    var r = proxyOn() ? await window.AG_ADMIN.lib("/admin/books") : await fetch(WORKER + "/admin/books?password=" + encodeURIComponent(PW));
    return r.ok ? r.json() : { books: [], categories: [] };
  }
  async function listStats() {
    var r = proxyOn() ? await window.AG_ADMIN.lib("/admin/analytics") : await fetch(WORKER + "/admin/analytics?password=" + encodeURIComponent(PW));
    return r.ok ? r.json() : null;
  }
  async function postJson(route, payload) {
    var r;
    if (proxyOn()) {
      // Don't send the shared password to the proxy, the Worker injects it.
      var clean = Object.assign({}, payload || {}); delete clean.password;
      r = await window.AG_ADMIN.lib(route, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(clean) });
    } else {
      r = await fetch(WORKER + route, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(payload) });
    }
    var j = await r.json().catch(function () { return {}; });
    if (!r.ok || j.error) throw new Error(JSON.stringify(j.error || j));
    return j;
  }

  // PDF -> webp -> R2; returns {page_count, aspect}.
  // PRIMARY renderer = MuPDF (WASM): it colour-manages ICC/CMYK correctly, so
  // print PDFs no longer come out PINK (pdf.js doesn't colour-manage, that was
  // the "Built For Life"/"Fat Burning" bug). Falls back to pdf.js if MuPDF can't
  // load, so the uploader never hard-fails.
  var _mupdf = null, _mupdfTried = false;
  async function getMupdf() {
    if (_mupdfTried) return _mupdf;
    _mupdfTried = true;
    try { _mupdf = await import("https://cdn.jsdelivr.net/npm/mupdf@1.27.0/dist/mupdf.js"); }
    catch (e) { console.warn("[library] MuPDF unavailable, using pdf.js (colours may shift on print PDFs)", e); _mupdf = null; }
    return _mupdf;
  }

  async function convert(file, book, onProgress) {
    var mupdf = await getMupdf();
    if (mupdf) {
      try { return await convertMupdf(mupdf, file, book, onProgress); }
      catch (e) { console.warn("[library] MuPDF render failed, falling back to pdf.js", e); }
    }
    return await convertPdfjs(file, book, onProgress);
  }

  async function pngBytesToCanvas(bytes) {
    var bmp = await createImageBitmap(new Blob([bytes], { type: "image/png" }));
    var c = document.createElement("canvas"); c.width = bmp.width; c.height = bmp.height;
    c.getContext("2d").drawImage(bmp, 0, 0);
    if (bmp.close) bmp.close();
    return c;
  }

  async function uploadResolutions(book, i, cFull) {
    await uploadBlob(book, "full/" + pad4(i) + ".webp", await toWebp(cFull, 0.82), "image/webp");
    var sizes = [["std", 1200, 0.8], ["thumb", 600, 0.7]];
    for (var s = 0; s < sizes.length; s++) {
      var w = sizes[s][1], h = Math.round(cFull.height * (w / cFull.width));
      var c = document.createElement("canvas"); c.width = w; c.height = h;
      c.getContext("2d").drawImage(cFull, 0, 0, w, h);
      await uploadBlob(book, sizes[s][0] + "/" + pad4(i) + ".webp", await toWebp(c, sizes[s][2]), "image/webp");
    }
  }

  async function convertMupdf(mupdf, file, book, onProgress) {
    var ab = await file.arrayBuffer();
    var doc = mupdf.Document.openDocument(new Uint8Array(ab), "application/pdf");
    var n = doc.countPages(), aspect = "1414:2000";
    for (var i = 0; i < n; i++) {
      var page = doc.loadPage(i);
      var b = page.getBounds();
      var pw = b[2] - b[0], ph = b[3] - b[1];
      if (i === 0) aspect = Math.round(pw) + ":" + Math.round(ph);
      var pix = page.toPixmap(mupdf.Matrix.scale(2000 / pw, 2000 / pw), mupdf.ColorSpace.DeviceRGB, false);
      var cFull = await pngBytesToCanvas(pix.asPNG());
      if (pix.destroy) pix.destroy();       // free WASM heap, 48 big pixmaps would OOM otherwise
      if (page.destroy) page.destroy();
      await uploadResolutions(book, i + 1, cFull);
      if (onProgress) onProgress(i + 1, n);
    }
    if (doc.destroy) doc.destroy();
    await uploadBlob(book, "source.pdf", file, "application/pdf");
    return { page_count: n, aspect: aspect };
  }

  async function convertPdfjs(file, book, onProgress) {
    var ab = await file.arrayBuffer();
    var pdf = await window.pdfjsLib.getDocument({ data: ab }).promise;
    var n = pdf.numPages, aspect = "1414:2000";
    for (var i = 1; i <= n; i++) {
      var page = await pdf.getPage(i);
      var vp1 = page.getViewport({ scale: 1 });
      if (i === 1) aspect = Math.round(vp1.width) + ":" + Math.round(vp1.height);
      var vp = page.getViewport({ scale: 2000 / vp1.width });
      var cFull = document.createElement("canvas"); cFull.width = Math.round(vp.width); cFull.height = Math.round(vp.height);
      await page.render({ canvasContext: cFull.getContext("2d"), viewport: vp }).promise;
      await uploadResolutions(book, i, cFull);
      if (onProgress) onProgress(i, n);
    }
    await uploadBlob(book, "source.pdf", file, "application/pdf");
    return { page_count: n, aspect: aspect };
  }

  function catList() { return '<datalist id="agla-cats">' + CATS.map(function (c) { return '<option value="' + esc(c) + '">'; }).join("") + "</datalist>"; }
  /* Category <select> of existing categories + an "add new" escape hatch.
     Works even when the books list is empty (just shows Select + Add new). */
  function catOptions(current) {
    var has = current && CATS.indexOf(current) < 0; // a saved value not in the list
    var opts = '<option value=""' + (current ? "" : " selected") + '>Select category, </option>';
    CATS.forEach(function (c) { opts += '<option value="' + esc(c) + '"' + (c === current ? " selected" : "") + ">" + esc(c) + "</option>"; });
    if (has) opts += '<option value="' + esc(current) + '" selected>' + esc(current) + "</option>";
    opts += '<option value="__new__">+ Add new category…</option>';
    return opts;
  }
  /* Toggle the sibling "new category" input visible only when "+ Add new…"
     is picked. newWrap is the wrapping <label> (so its caption hides too). */
  function wireCatSelect(sel, newWrap) {
    var inp = newWrap.querySelector("input");
    function sync() { var isNew = sel.value === "__new__"; newWrap.hidden = !isNew; if (isNew) inp.focus(); }
    sel.addEventListener("change", sync); sync();
  }
  function catVal(sel, newWrap) { return sel.value === "__new__" ? newWrap.querySelector("input").value.trim() : sel.value; }

  /* ── screens ──────────────────────────────────────────────────────────── */
  function lockScreen(msg) {
    root.innerHTML = "";
    var box = el('<div class="agla-card agla-lock"><h1>Stealth Library</h1><p class="agla-sub">Staff access</p>' +
      (msg ? '<p class="agla-err">' + msg + "</p>" : "") +
      '<input type="password" id="agla-pw" placeholder="Admin password"><button class="agla-btn" id="agla-enter">Enter</button></div>');
    root.appendChild(box);
    var input = box.querySelector("#agla-pw");
    var go = async function () { var pw = input.value.trim(); if (!pw) return; if (await ping(pw)) { PW = pw; app("add"); } else lockScreen("Wrong password."); };
    box.querySelector("#agla-enter").addEventListener("click", go);
    input.addEventListener("keydown", function (e) { if (e.key === "Enter") go(); });
    input.focus();
  }

  async function app(tab) {
    root.innerHTML = "";
    var data = await listBooks(); CATS = data.categories || [];
    var shell = el('<div class="agla-card"><div class="agla-tabs">' +
      '<button class="agla-tab" data-t="add">Add book</button>' +
      '<button class="agla-tab" data-t="manage">Manage (' + (data.books || []).length + ')</button>' +
      '<button class="agla-tab" data-t="order">Reorder</button>' +
      '<button class="agla-tab" data-t="stats">Analytics</button>' +
      '<button class="agla-tab" data-t="info">How it works</button>' +
      '</div><div id="agla-view"></div></div>');
    root.appendChild(shell);
    shell.querySelectorAll(".agla-tab").forEach(function (b) {
      b.classList.toggle("is-on", b.dataset.t === tab);
      b.addEventListener("click", function () { app(b.dataset.t); });
    });
    var view = shell.querySelector("#agla-view");
    if (tab === "info") renderInfo(view);
    else if (tab === "manage") renderManage(view, data);
    else if (tab === "order") renderOrder(view, data);
    else if (tab === "stats") renderStats(view, data);
    else renderAdd(view);
  }

  function renderInfo(view) {
    view.innerHTML = '<div style="max-width:780px;font-size:13px;line-height:1.55;color:var(--ag-text);">' +
      '<h3 style="color:var(--ag-text);font-size:16px;margin:0 0 8px;">What this is</h3>' +
      '<p>Staff tool for adding new books (PDFs) to the AG Library at <a href="/pages/library" target="_blank">/pages/library</a>. ' +
      'Each book is published as a real Shopify product with a custom PDF reader page that paywalls everything past the preview pages.</p>' +

      '<h3 style="color:var(--ag-text);font-size:16px;margin:20px 0 8px;">How "Add book" works</h3>' +
      '<ol>' +
      '<li><strong>You pick a PDF.</strong> The browser converts each page to WebP using <code>pdf.js</code>no server-side conversion needed.</li>' +
      '<li><strong>Pages upload to Cloudflare R2</strong> via the password-gated Worker. R2 is the storage; the public URLs are served via the Worker.</li>' +
      '<li><strong>Shopify product is created</strong> with the book metadata, price, compare-at, preview-page count, and category.</li>' +
      '<li><strong>Library page picks it up automatically</strong>: no theme deploy needed.</li>' +
      '</ol>' +

      '<h3 style="color:var(--ag-text);font-size:16px;margin:20px 0 8px;">Fields explained</h3>' +
      '<dl>' +
      '<dt><strong>Title</strong></dt><dd>Customer-facing title. Shows on the library shelf + the PDF reader page.</dd>' +
      '<dt><strong>Book ID / handle</strong></dt><dd>URL slug, auto-derived from the title; override if you want a shorter URL. Becomes /products/&lt;handle&gt;.</dd>' +
      '<dt><strong>Price / Compare-at</strong></dt><dd>Shopify-standard pricing. Compare-at shows the strike-through "was" price.</dd>' +
      '<dt><strong>Free preview pages</strong></dt><dd>How many pages a non-buyer can read before the paywall. 3 is typical.</dd>' +
      '<dt><strong>Category</strong></dt><dd>Which shelf the book appears on at /pages/library. Pick an existing category or type a new one.</dd>' +
      '</dl>' +

      '<h3 style="color:var(--ag-text);font-size:16px;margin:20px 0 8px;">Manage tab</h3>' +
      '<p>Lists every published book + lets you re-categorise / delete. Deleting REMOVES the Shopify product AND the R2 files; customers who bought it lose access. Move to a "Hidden" category instead if you want a soft remove.</p>' +

      '<h3 style="color:var(--ag-text);font-size:16px;margin:20px 0 8px;">Troubleshooting</h3>' +
      '<dl>' +
      '<dt><strong>Upload fails halfway through</strong></dt><dd>Browser tabs throttle background uploads. Keep this tab focused while uploading. If a page upload genuinely fails, refresh and re-run, already-uploaded pages aren\'t re-uploaded.</dd>' +
      '<dt><strong>"Wrong password"</strong></dt><dd>The Worker has the same brute-force protection as other admin tools, after 8 wrong tries, 15-min lockout.</dd>' +
      '<dt><strong>Library page doesn\'t show the new book</strong></dt><dd>Hard-refresh /pages/library. Cache TTL is 5 min on the storefront.</dd>' +
      '</dl>' +

      '<h3 style="color:var(--ag-text);font-size:16px;margin:20px 0 8px;">Where data lives</h3>' +
      '<p>PDFs converted to WebP in browser → uploaded to Cloudflare R2 → served via the AG Library Worker. Product metadata lives in Shopify (price, title, handle, custom metafields for preview count + page count).</p>' +
      '</div>';
  }

  function money(n, cur) {
    var sym = cur === "GBP" ? "£" : cur === "EUR" ? "€" : cur === "USD" ? "$" : "";
    return sym + (Number(n) || 0).toFixed(2) + (sym ? "" : " " + (cur || ""));
  }
  function fmtDate(iso) { if (!iso) return "-"; try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); } catch (e) { return String(iso).slice(0, 10); } }
  function statCard(label, val) { return '<div class="agla-stat-card"><div class="agla-stat-val">' + val + '</div><div class="agla-stat-lbl">' + label + "</div></div>"; }

  function renderOrder(view, data) {
    var books = (data.books || []).slice();
    if (books.length < 2) { view.innerHTML = '<p class="agla-hint">Add at least two books to reorder the shelf.</p>'; return; }
    view.innerHTML =
      '<p class="agla-hint" style="margin-bottom:1rem;">Drag the books into the order they should appear on <a class="agla-link" href="/pages/library" target="_blank">the shelf</a>, then Save.</p>' +
      '<ul class="agla-order" id="agla-order">' +
      books.map(function (b) {
        return '<li class="agla-order-row" draggable="true" data-pid="' + esc(b.productId) + '">' +
          '<span class="agla-order-grip" aria-hidden="true">⠿</span>' +
          '<img class="agla-order-thumb" src="/apps/library/page?book=' + encodeURIComponent(b.book_id) + '&n=1&res=thumb" alt="" width="40" height="56" loading="lazy">' +
          '<span class="agla-order-title">' + esc(b.title) + "</span></li>";
      }).join("") +
      "</ul>" +
      '<button class="agla-btn" id="agla-order-save">Save order</button>' +
      '<div class="agla-result" id="agla-order-res" hidden></div>';

    var list = view.querySelector("#agla-order");
    var dragEl = null;
    list.querySelectorAll(".agla-order-row").forEach(function (row) {
      row.addEventListener("dragstart", function () { dragEl = row; setTimeout(function () { row.classList.add("is-drag"); }, 0); });
      row.addEventListener("dragend", function () { row.classList.remove("is-drag"); dragEl = null; });
    });
    list.addEventListener("dragover", function (e) {
      e.preventDefault();
      if (!dragEl) return;
      var after = afterEl(list, e.clientY);
      if (after == null) list.appendChild(dragEl); else list.insertBefore(dragEl, after);
    });
    function afterEl(container, y) {
      var els = [].slice.call(container.querySelectorAll(".agla-order-row:not(.is-drag)"));
      var best = { off: -Infinity, el: null };
      els.forEach(function (child) {
        var box = child.getBoundingClientRect();
        var off = y - box.top - box.height / 2;
        if (off < 0 && off > best.off) best = { off: off, el: child };
      });
      return best.el;
    }
    view.querySelector("#agla-order-save").addEventListener("click", async function () {
      var btn = this; btn.disabled = true;
      var order = [].slice.call(list.querySelectorAll(".agla-order-row")).map(function (r) { return r.dataset.pid; });
      try {
        await postJson("/admin/reorder", { password: PW, order: order });
        var res = view.querySelector("#agla-order-res"); res.hidden = false;
        res.innerHTML = "<strong>✓ Order saved.</strong> The shelf updates within a moment (hard-refresh /pages/library to see it).";
      } catch (e) { alert("Error: " + (e && e.message)); } finally { btn.disabled = false; }
    });
  }

  async function renderStats(view, data) {
    view.innerHTML = '<p class="agla-hint">Loading analytics…</p>';
    var s = await listStats();
    if (!s) { view.innerHTML = '<p class="agla-err">Couldn\'t load analytics. Check the admin password / Worker.</p>'; return; }
    var titles = {}; (data.books || []).forEach(function (b) { titles[b.book_id] = b.title; });
    var cur = s.currency || "GBP";
    var cards = '<div class="agla-stat-cards">' +
      statCard("Buyers", s.totals.buyers) +
      statCard("Orders", s.totals.orders) +
      statCard("Revenue", money(s.totals.revenue, cur)) +
      statCard("Downloads", s.totals.downloads ? (s.totals.downloads.pdf + s.totals.downloads.easyprint) : 0) +
      "</div>";
    var table;
    if (s.books && s.books.length) {
      var rows = s.books.map(function (b) {
        var dl = b.downloads ? (b.downloads.pdf + " / " + b.downloads.easyprint) : "0";
        return "<tr><td>" + esc(titles[b.book_id] || b.book_id) + "</td><td>" + b.buyers + "</td><td>" + b.units +
          "</td><td>" + b.orders + "</td><td>" + money(b.revenue, cur) + "</td><td>" + dl + "</td><td>" + fmtDate(b.last) + "</td></tr>";
      }).join("");
      table = '<table class="agla-stat-table"><thead><tr><th>Book</th><th>Buyers</th><th>Units</th><th>Orders</th><th>Revenue</th><th title="PDF / easy-print downloads by owners">Downloads</th><th>Last sale</th></tr></thead><tbody>' + rows + "</tbody></table>";
    } else { table = '<p class="agla-hint">No sales yet.</p>'; }
    var exc = (s.totals.testOrders || 0) + (s.totals.unpaidOrders || 0);
    view.innerHTML = cards + table +
      '<p class="agla-hint" style="margin-top:1rem;"><strong>Real paid sales only.</strong> Test orders and unpaid &pound;0 orders (free grants / draft orders marked paid) are excluded' +
      (exc > 0 ? ' (' + exc + ' excluded)' : '') +
      '. Buyers, units &amp; revenue come directly from the actual orders: discounted line totals, net of refunds. Downloads = PDF / easy-print downloads by owners.</p>';
  }

  function renderAdd(view) {
    view.innerHTML =
      '<div class="agla-form">' +
      '<label>PDF file<input type="file" id="f-pdf" accept="application/pdf"></label>' +
      '<label>Title<input type="text" id="f-title" placeholder="The Complete Home Gym Strength Plan"></label>' +
      '<label>Book ID / handle <span class="agla-hint">(auto from title)</span><input type="text" id="f-id"></label>' +
      '<div class="agla-row"><label>Price (£)<input type="number" id="f-price" step="0.01" min="0"></label>' +
      '<label>Compare-at (£) <span class="agla-hint">(was-price)</span><input type="number" id="f-compare" step="0.01" min="0"></label></div>' +
      '<div class="agla-row"><label>Free preview pages<input type="number" id="f-preview" value="3" min="0"></label>' +
      '<label>Category<select id="f-cat">' + catOptions("") + '</select></label></div>' +
      '<label id="f-cat-new-l" hidden>New category name<input type="text" id="f-cat-new" placeholder="e.g. Guides"></label>' +
      '<button class="agla-btn" id="f-go">Convert &amp; publish</button>' +
      '<div class="agla-progress" id="f-prog" hidden><div class="agla-bar"><span id="f-fill"></span></div><div class="agla-stat" id="f-stat"></div></div>' +
      '<div class="agla-result" id="f-res" hidden></div>' +
      '</div>';
    var t = view.querySelector("#f-title"), id = view.querySelector("#f-id");
    t.addEventListener("input", function () { if (!id.dataset.touched) id.value = slug(t.value); });
    id.addEventListener("input", function () { id.dataset.touched = "1"; });
    wireCatSelect(view.querySelector("#f-cat"), view.querySelector("#f-cat-new-l"));
    view.querySelector("#f-go").addEventListener("click", function () { runAdd(view); });
  }

  function prog(view, pct, msg) { view.querySelector("#f-prog").hidden = false; view.querySelector("#f-fill").style.width = Math.round(pct) + "%"; view.querySelector("#f-stat").textContent = msg; }

  async function runAdd(view) {
    var file = view.querySelector("#f-pdf").files[0];
    var title = view.querySelector("#f-title").value.trim();
    var book = slug(view.querySelector("#f-id").value || title);
    if (!file) return alert("Choose a PDF."); if (!title) return alert("Enter a title."); if (!book) return alert("Enter a book ID.");
    var go = view.querySelector("#f-go"); go.disabled = true;
    try {
      prog(view, 2, "Reading PDF…");
      var meta = await convert(file, book, function (i, n) { prog(view, (i / (n + 1)) * 92 + 2, "Page " + i + " / " + n + " uploaded…"); });
      prog(view, 97, "Creating product…");
      var j = await postJson("/admin/create-book", {
        password: PW, book: book, title: title, handle: book,
        price: view.querySelector("#f-price").value, compare_at: view.querySelector("#f-compare").value,
        preview: parseInt(view.querySelector("#f-preview").value || "0", 10),
        category: catVal(view.querySelector("#f-cat"), view.querySelector("#f-cat-new-l")), page_count: meta.page_count, aspect: meta.aspect,
      });
      prog(view, 100, "Done.");
      var r = view.querySelector("#f-res"); r.hidden = false;
      r.innerHTML = '<strong>✓ Published:</strong> ' + esc(title) + " (" + meta.page_count + ' pages)<br><a class="agla-link" href="' + j.url + '" target="_blank">' + j.url + "</a>";
    } catch (e) { alert("Error: " + (e && e.message)); prog(view, 0, "Failed."); } finally { go.disabled = false; }
  }

  // Upload a print-friendly PDF for a book → R2 <book>/easyprint.pdf. No page
  // rendering needed (it's download-only, not the flipbook). The worker flags
  // the book so the reader shows the "Download easy print" button.
  async function uploadEasyPrint(row, b, file) {
    var stat = row.querySelector(".m-easyprint-stat");
    try {
      if (stat) stat.textContent = "…uploading";
      await uploadBlob(b.book_id, "easyprint.pdf", file, "application/pdf");
      if (stat) stat.textContent = "✓";
      b.has_easyprint = true;
    } catch (err) {
      if (stat) stat.textContent = "✗";
      alert("Easy-print upload failed: " + (err && err.message || err));
    }
  }

  function renderManage(view, data) {
    var books = data.books || [];
    if (!books.length) { view.innerHTML = '<p class="agla-hint">No books yet. Add one from the “Add book” tab.</p>'; return; }
    view.innerHTML = catList() + books.map(function (b, i) {
      return '<div class="agla-book" data-i="' + i + '">' +
        '<div class="agla-book-h">' +
        '<img class="agla-book-thumb" src="/apps/library/page?book=' + encodeURIComponent(b.book_id) + '&n=1&res=thumb" alt="" width="44" height="62" loading="lazy">' +
        '<div class="agla-book-title"><strong>' + esc(b.title) + '</strong> <span class="agla-hint">' + (b.page_count || "?") + ' pp · ' + esc(b.book_id) + '</span>' +
        (b.downloads ? ' <span class="agla-dlcount" title="Download tally">↓ ' + (b.downloads.pdf || 0) + ' PDF' + (b.has_easyprint || b.downloads.easyprint ? ' · ' + (b.downloads.easyprint || 0) + ' easy-print' : '') + '</span>' : '') + "</div>" +
        '<div class="agla-book-actions">' +
        '<a class="agla-dl" href="#" data-dl-book="' + esc(b.book_id) + '" title="Download the source PDF">PDF ↓</a>' +
        '<select class="agla-status is-' + (b.status || "active").toLowerCase() + '" title="Storefront visibility: Live (shelf + page), Unlisted (page only, off the shelf), Draft (fully hidden)">' +
          '<option value="ACTIVE"' + (b.status === "ACTIVE" ? " selected" : "") + '>Live</option>' +
          '<option value="UNLISTED"' + (b.status === "UNLISTED" ? " selected" : "") + '>Unlisted</option>' +
          '<option value="DRAFT"' + (b.status === "DRAFT" ? " selected" : "") + '>Draft</option>' +
        '</select>' +
        '<button type="button" class="agla-setcover" title="Use the page-1 preview as the product image">Set image</button>' +
        '<button type="button" class="agla-del" title="Delete this book">Delete</button>' +
        "</div></div>" +
        '<label>Title<input class="m-title" type="text" value="' + esc(b.title) + '"></label>' +
        '<div class="agla-row"><label>Price (£)<input class="m-price" type="number" step="0.01" value="' + esc(b.price || "") + '"></label>' +
        '<label>Compare-at (£)<input class="m-compare" type="number" step="0.01" value="' + esc(b.compare_at || "") + '"></label></div>' +
        '<div class="agla-row"><label>Preview pages<input class="m-preview" type="number" value="' + esc(b.preview || "") + '"></label>' +
        '<label>Category<select class="m-cat">' + catOptions(b.category || "") + '</select></label></div>' +
        '<label class="m-cat-new-l" hidden>New category name<input type="text" placeholder="e.g. Guides"></label>' +
        '<div class="agla-row"><button class="agla-btn m-save">Save changes</button>' +
        '<label class="m-replace-l">Replace PDF<input class="m-replace" type="file" accept="application/pdf"></label>' +
        '<label class="m-replace-l">Easy-print PDF <span class="m-easyprint-stat">' + (b.has_easyprint ? "✓" : "") + '</span><input class="m-easyprint" type="file" accept="application/pdf"></label></div>' +
        '<div class="agla-progress m-prog" hidden><div class="agla-bar"><span></span></div><div class="agla-stat"></div></div>' +
        "</div>";
    }).join("");
    view.querySelectorAll(".agla-book").forEach(function (row) {
      var b = books[parseInt(row.dataset.i, 10)];
      wireCatSelect(row.querySelector(".m-cat"), row.querySelector(".m-cat-new-l"));
      row.querySelector(".m-save").addEventListener("click", function () { saveBook(row, b); });
      row.querySelector(".m-replace").addEventListener("change", function (e) { if (e.target.files[0]) replacePdf(row, b, e.target.files[0]); });
      row.querySelector(".m-easyprint").addEventListener("change", function (e) { if (e.target.files[0]) uploadEasyPrint(row, b, e.target.files[0]); });
      row.querySelector(".agla-del").addEventListener("click", function () { deleteBook(row, b); });
      row.querySelector(".agla-setcover").addEventListener("click", function () { setCover(row, b); });
      row.querySelector(".agla-status").addEventListener("change", function (e) { setStatus(row, b, e.target.value); });
      var dl = row.querySelector(".agla-dl");
      if (dl) dl.addEventListener("click", function (e) { e.preventDefault(); downloadSource(b.book_id, dl); });
    });
  }

  // Source-PDF download. With the OTP shell, fetch through the token-authed
  // proxy and save the blob (a plain <a> navigation can't carry the Bearer
  // header). Without the shell, fall back to the legacy direct password URL.
  async function downloadSource(bookId, link) {
    if (!proxyOn()) {
      window.open(WORKER + "/admin/source?password=" + encodeURIComponent(PW) + "&book=" + encodeURIComponent(bookId), "_blank", "noopener");
      return;
    }
    var old = link ? link.textContent : "";
    if (link) link.textContent = "…";
    try {
      var r = await window.AG_ADMIN.lib("/admin/source?book=" + encodeURIComponent(bookId));
      if (!r.ok) throw new Error("HTTP " + r.status);
      var blob = await r.blob();
      var cd = r.headers.get("content-disposition") || "";
      var m = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
      var name = (m && decodeURIComponent(m[1].replace(/"$/, ""))) || (bookId + ".pdf");
      var u = URL.createObjectURL(blob);
      var a = document.createElement("a"); a.href = u; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(function () { URL.revokeObjectURL(u); }, 8000);
    } catch (e) {
      alert("Download failed: " + (e && e.message || e));
    } finally {
      if (link) link.textContent = old;
    }
  }

  async function saveBook(row, b, extra) {
    var btn = row.querySelector(".m-save"); btn.disabled = true;
    try {
      var payload = {
        password: PW, productId: b.productId, variantId: b.variantId, book_id: b.book_id,
        title: row.querySelector(".m-title").value.trim(),
        price: row.querySelector(".m-price").value, compare_at: row.querySelector(".m-compare").value,
        preview: row.querySelector(".m-preview").value, category: catVal(row.querySelector(".m-cat"), row.querySelector(".m-cat-new-l")),
      };
      if (extra) { payload.page_count = extra.page_count; payload.aspect = extra.aspect; }
      await postJson("/admin/update-book", payload);
      btn.textContent = "Saved ✓"; setTimeout(function () { btn.textContent = "Save changes"; }, 1500);
    } catch (e) { alert("Error: " + (e && e.message)); } finally { btn.disabled = false; }
  }

  async function replacePdf(row, b, file) {
    var pr = row.querySelector(".m-prog"), fill = pr.querySelector("span"), stat = pr.querySelector(".agla-stat");
    pr.hidden = false;
    try {
      var meta = await convert(file, b.book_id, function (i, n) { fill.style.width = Math.round((i / n) * 100) + "%"; stat.textContent = "Page " + i + " / " + n + "…"; });
      stat.textContent = "Saving…";
      await saveBook(row, b, meta);
      stat.textContent = "Replaced (" + meta.page_count + " pages). Saved.";
    } catch (e) { alert("Error: " + (e && e.message)); stat.textContent = "Failed."; }
  }

  async function setCover(row, b) {
    var btn = row.querySelector(".agla-setcover"); var t = btn.textContent; btn.disabled = true; btn.textContent = "Setting…";
    try {
      await postJson("/admin/set-cover", { password: PW, productId: b.productId, book_id: b.book_id, title: b.title });
      btn.textContent = "Set ✓"; setTimeout(function () { btn.textContent = t; btn.disabled = false; }, 1500);
    } catch (e) { alert("Error: " + (e && e.message)); btn.textContent = t; btn.disabled = false; }
  }

  // Set a book's storefront visibility via its Shopify product status:
  // ACTIVE (Live, shelf + page), UNLISTED (page only, off the shelf), or
  // DRAFT (fully hidden). Driven by the per-book status dropdown.
  async function setStatus(row, b, status) {
    var sel = row.querySelector(".agla-status");
    var prev = b.status; sel.disabled = true;
    try {
      await postJson("/admin/product-status", { password: PW, productId: b.productId, status: status });
      b.status = status;
      sel.className = "agla-status is-" + status.toLowerCase();
    } catch (err) {
      alert("Status change failed: " + (err && err.message || err));
      sel.value = prev || "ACTIVE";
    } finally { sel.disabled = false; }
  }

  async function deleteBook(row, b) {
    if (!confirm('Delete "' + b.title + '"?\n\nThis permanently removes the product AND its files. Anyone who bought it loses access. This cannot be undone.')) return;
    var btn = row.querySelector(".agla-del"); btn.disabled = true; btn.textContent = "Deleting…";
    try {
      await postJson("/admin/delete-book", { password: PW, productId: b.productId, book_id: b.book_id });
      row.remove();
    } catch (e) { alert("Error: " + (e && e.message)); btn.disabled = false; btn.textContent = "Delete"; }
  }

  /* ── boot ─────────────────────────────────────────────────────────────── */
  /* The lock screen keeps a typed password in memory only. It used to save it
     to localStorage (agLibAdminPwd), where it outlived the tab; any copy left
     by an older build is cleared on load. */
  function forgetLegacyPwd() { try { localStorage.removeItem("agLibAdminPwd"); } catch (e) {} }

  /* With the OTP shell present every call goes through the auth Worker's
     proxy, which authenticates by session token and ignores any typed
     password, so a lock screen there could never work. A failed probe means no
     Library access for this session, or the proxy could not be reached: say
     that, with a retry, and never fall back to a password prompt. */
  function noAccess() {
    root.innerHTML = "";
    var box = el('<div class="agla-card agla-lock"><h1>Stealth Library</h1>' +
      '<p class="agla-err">Your account does not have access to the Library, or it could not be reached just now.</p>' +
      '<p class="agla-sub">Ask an admin for Library access if you need it.</p>' +
      '<button class="agla-btn" id="agla-retry">Try again</button></div>');
    root.appendChild(box);
    box.querySelector("#agla-retry").addEventListener("click", function () { window.location.reload(); });
  }

  ready(function () {
    root = document.getElementById("agla-root"); if (!root) return;
    forgetLegacyPwd();
    if (window.pdfjsLib) window.pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js";
    // With the OTP shell present, use the session's staff password, no prompt.
    if (window.AG_ADMIN && typeof window.AG_ADMIN.onReady === "function") {
      window.AG_ADMIN.onReady(function () {
        // PW is now only used by the shell-absent (direct) fallback paths; every
        // proxied call (incl. the source download) authenticates by token.
        try { PW = (window.AG_ADMIN.pwd && window.AG_ADMIN.pwd()) || ""; } catch (e) {}
        // Probe the ag-library proxy with the OTP token; on success load, else
        // tell the user they have no access (no password fallback).
        ping().then(function (ok) { if (ok) app("add"); else noAccess(); }).catch(function () { noAccess(); });
      });
    } else {
      lockScreen();
    }
  });
})();
